import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import LoadingSpinner from "../../components/LoadingSpinner";
import {
    getAcceptAuthConfig, getJSONConfig,
    PARTNER_TYPES_WORKSHOP_ENDPOINT,
    WORKSHOPS_INFO_ENDPOINT
} from "../../api/api";

const useDataCache = () => {
    const cacheRef = React.useRef({
        workshops: null,
        partners: new Map()
    });

    const clearPartnersCache = () => {
        cacheRef.current.partners = new Map();
    };

    return { cache: cacheRef.current, clearPartnersCache };
};

const MainPartnersPage = () => {
    const { cache, clearPartnersCache } = useDataCache();
    const [workshops, setWorkshops] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [partnerTypes, setPartnerTypes] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        clearPartnersCache();
        return () => {
            clearPartnersCache();
        };
    }, []);

    useEffect(() => {
        if (cache.workshops) {
            setWorkshops(cache.workshops);
            handleInitialSelection(cache.workshops);
            setIsLoading(false);
            return;
        }

        axios.get(WORKSHOPS_INFO_ENDPOINT, getJSONConfig())
            .then(response => {
                const workshopsData = response.data.sort((a, b) => b.year - a.year);
                cache.workshops = workshopsData;
                setWorkshops(workshopsData);
                handleInitialSelection(workshopsData);
            })
            .catch(error => {
                console.error('Ошибка при загрузке данных мастерских:', error);
            });
    }, [cache]);

    const handleInitialSelection = (workshopsData) => {
        if (workshopsData.length > 0) {
            const activeWorkshop = workshopsData.find(w => w.is_active);
            const workshop = activeWorkshop || workshopsData[0];
            setSelectedYear(workshop.year);
            loadPartners(workshop.id);
        }
    };

    const loadPartners = (workshopId) => {
        if (cache.partners.has(workshopId)) {
            setPartnerTypes(cache.partners.get(workshopId));
            setIsLoading(false);
            setIsInitialLoad(false);
            return;
        }

        axios.get(PARTNER_TYPES_WORKSHOP_ENDPOINT(workshopId), getJSONConfig())
            .then(response => {
                const partnersData = response.data;
                cache.partners.set(workshopId, partnersData);
                setPartnerTypes(partnersData);
            })
            .catch(error => {
                console.error('Ошибка при загрузке партнеров:', error);
            })
            .finally(() => {
                setIsLoading(false);
                setIsInitialLoad(false);
            });
    };

    const handleYearChange = (year) => {
        setSelectedYear(year);
        setIsLoading(true);
        const workshop = workshops.find(w => w.year === year);
        if (workshop) {
            loadPartners(workshop.id);
        }
    };

    const uniqueYears = useMemo(() => {
        return [...new Set(workshops.map(w => w.year))].sort((a, b) => b - a);
    }, [workshops]);

    if (isInitialLoad) {
        return <LoadingSpinner />;
    }

    return (
        <div className="partners-page">
            <div className="years-subheader">
                <div className="container">
                    <div className="years-navigation">
                        <div className="years-list">
                            {uniqueYears.map((year, index) => (
                                <React.Fragment key={year}>
                                    <span
                                        className={`year-tab ${selectedYear === year ? 'active' : ''}`}
                                        onClick={() => handleYearChange(year)}
                                    >
                                        {year}
                                    </span>
                                    {index < uniqueYears.length - 1 && (
                                        <span className="year-separator">|</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="partners-content container">
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    partnerTypes.map(type => (
                        <div key={type.id} className="partner-type-section">
                            <h2>{type.name}</h2>
                            {type.description && <p className="bmm-text">{type.description}</p>}

                            <div className="partners-grid">
                                {type.partners.map(partner => (
                                    <a
                                        key={partner.id}
                                        href={partner.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="partner-card"
                                    >
                                        <div className="partner-logo-container">
                                            <img
                                                src={partner.photo}
                                                alt={partner.url}
                                                className="partner-logo"
                                            />
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default MainPartnersPage;